import { type User } from '@auth0/auth0-react';
import { type Role, RoleType } from '@qdrant/qdrant-cloud-public-api/iam/v1';

const namespace = 'https://cloud.qdrant.io';
const roleClaim = `${namespace}/role`;
const connectionClaim = `${namespace}/connection`;

export type Claims = Pick<User, 'name' | 'email' | 'given_name' | 'family_name'> & {
  /**
   * PLEASE NOTE: `Account.externalOwnerId == User.userId`. However, the current `sub` claim may not always equal `User.userId` (ownerId).
   *
   * The backend resolves the user identity using the `email` claim, matching against `User.email`. So, even if the `sub` claim changes
   * (e.g., user logs in with a different provider), the user will still be recognized as the same user based on their email.
   */
  sub?: string;
  loginCount?: number;
  [roleClaim]?: string[];
  [connectionClaim]?: string;
};

type ClaimName = 'role' | 'connection';

/**
 * Checks if the user has the QdrantDeveloper Role.
 */
export const hasAuth0DeveloperRole = (user?: User): boolean =>
  (user != null && getUserClaim(user, 'role')?.includes('QdrantDeveloper')) ?? false;

export const hasQdrantRbacDeveloperRole = (roles?: Role[]): boolean =>
  roles?.some((role) => role.roleType === RoleType.SYSTEM && role.name === 'Developer') ?? false;

export const getConnectionName = (user: User): string | undefined => getUserClaim(user, 'connection');

/**
 * Get a claim from the user object. It reads the user as `Claims`, not as `User`: `User` carries
 * an `[key: string]: any` index signature, so every claim comes back as `any` and the return type
 * has to come from the declaration above rather than from a type parameter the caller picks.
 */
function getUserClaim<T extends ClaimName>(user: Claims, claim: T): Claims[`${typeof namespace}/${T}`] {
  return user[`${namespace}/${claim}`];
}
